import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Sparkles, CheckCircle2, Hourglass, ShieldCheck, Lightbulb } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

/**
 * PerceptionContextSnippet (admin / counselor visible only)
 * ----------------------------------------------------------
 * Pinned read-only summary of the subscriber's completed ReGenflō Shift,
 * surfaced at the top of the Counselor Follow-Up form so counselors can
 * reinforce biological participation instead of symptom-chasing.
 *
 * Pure read of `perception_onboarding_status` via the existing admin endpoint.
 * - No new subscriber UI
 * - No new collection
 * - No writes
 */
export default function PerceptionContextSnippet({ subscriberId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subscriberId) { setLoading(false); return; }
    let mounted = true;
    axios.get(`${API}/perception-onboarding/admin/${subscriberId}`)
      .then(r => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [subscriberId]);

  if (loading || !data) return null;

  const status = data.status || 'not_started';
  const reflections = data.selected_reflections || {};
  const oldModel = reflections.old_model_patterns || [];
  const focus = reflections.priority_focus || [];
  const completedAt = data.completed_at
    ? new Date(data.completed_at).toLocaleDateString()
    : null;

  // If the subscriber has not started, surface a quiet hint and exit
  if (status === 'not_started') {
    return (
      <div
        className="rounded-xl border border-dashed border-[#E3DFD7] bg-[#FBFAF7] px-4 py-3 flex items-start gap-2"
        data-testid="perception-snippet-not-started"
      >
        <Hourglass className="h-3.5 w-3.5 text-[#8A8681] mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-[#5C5955] font-['Work_Sans']">
          This subscriber has not yet begun the ReGenflō Shift. Perception context will appear here once completed.
        </p>
      </div>
    );
  }

  const isCompleted = status === 'completed';
  const StatusIcon = isCompleted ? CheckCircle2 : Sparkles;
  const statusLabel = isCompleted
    ? 'ReGenflō Shift completed'
    : status === 'in_progress' ? 'ReGenflō Shift in progress'
    : status === 'remind_later' ? 'ReGenflō Shift — reminded later'
    : 'ReGenflō Shift';

  return (
    <div
      className="rounded-xl border border-[#E3DFD7] bg-gradient-to-r from-[#FAF6EC] to-[#F7F1E2] p-4"
      data-testid="perception-context-snippet"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-7 w-7 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0">
          <StatusIcon className="h-3.5 w-3.5 text-[#8C6B55]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C6B55] font-['Work_Sans']">
            Perception Context
          </p>
          <p className="text-sm font-['Outfit'] text-[#2C2A29] leading-tight">{statusLabel}</p>
        </div>
        {completedAt ? (
          <Badge variant="outline" className="border-[#C8A87B] text-[#7A4A2B] text-[10px]" data-testid="perception-snippet-completed-at">
            Completed {completedAt}
          </Badge>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div data-testid="perception-snippet-old-model">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] font-['Work_Sans'] mb-1.5">
            Old-model patterns subscriber recognised
          </p>
          {oldModel.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {oldModel.map((p, i) => (
                <span
                  key={i}
                  className="inline-block px-2.5 py-1 rounded-full bg-white text-[#3B3936] text-[11px] font-['Work_Sans'] border border-[#E3DFD7]"
                >
                  {p}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-[#8A8681] italic font-['Work_Sans']">None selected.</p>
          )}
        </div>

        <div data-testid="perception-snippet-focus">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] font-['Work_Sans'] mb-1.5">
            Priority focus chosen
          </p>
          {focus.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {focus.map((p, i) => (
                <span
                  key={i}
                  className="inline-block px-2.5 py-1 rounded-full bg-[#2C2A29] text-white text-[11px] font-['Work_Sans']"
                >
                  {p}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-[#8A8681] italic font-['Work_Sans']">None selected.</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3" data-testid="perception-snippet-pledge">
        <ShieldCheck
          className={`h-3.5 w-3.5 ${data.pledge_accepted ? 'text-[#4E7A6B]' : 'text-[#B5B0A8]'}`}
          strokeWidth={1.5}
        />
        <p className="text-[11px] text-[#5C5955] font-['Work_Sans']">
          Participant pledge:{' '}
          <span className={data.pledge_accepted ? 'text-[#4E7A6B] font-medium' : 'text-[#8A8681]'}>
            {data.pledge_accepted ? 'Accepted' : 'Not accepted'}
          </span>
        </p>
      </div>

      <div
        className="rounded-md border-l-2 border-[#C89B7B] bg-white/60 p-2.5 flex items-start gap-2"
        data-testid="perception-snippet-counselor-cue"
      >
        <Lightbulb className="h-3.5 w-3.5 text-[#8C6B55] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <p className="text-[11.5px] text-[#3B3936] font-['Work_Sans'] italic leading-snug">
          Use this context to reinforce biological participation, not symptom-chasing.
        </p>
      </div>
    </div>
  );
}
